import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, Home, Users, MapPin, Activity, ListChecks, LogOut, User as UserIcon, ArrowLeft, Inbox, ListPlus, Briefcase, Rocket, ClipboardCheck, Network, MailCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import QuickCreateDropdown from './QuickCreateDropdown';
import { usePendingIntakeCount } from '../intake/IntakeReviewQueue';
import BusinessUnitSwitcher from '../launches/BusinessUnitSwitcher';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';

/**
 * Header unificado usado nas páginas novas (contatos, imóveis, sync, fila-empresas).
 * Agrupa navegação em Negócio / Cadastro / Operação, expõe dropdown +Novo,
 * identidade do usuário e logout.
 *
 * Dashboard ainda mantém seu header próprio (legado). Pode migrar depois.
 */
export default function AppHeader({ title, subtitle, icon: TitleIcon }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { pendingCount } = usePendingIntakeCount();
  const { availableUnits } = useBusinessUnit();
  const hasLaunches = Array.isArray(availableUnits) && availableUnits.includes('launches');

  const NavGroup = ({ label, items }) => (
    <div className="flex items-center gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold pr-1 hidden lg:inline">{label}</span>
      {items.map((it) => {
        const active = location.pathname === it.to;
        return (
          <Link
            key={it.to}
            to={it.to}
            className={`px-2.5 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${
              active ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-slate-600 hover:bg-slate-100'
            }`}
            title={it.hint}
          >
            <it.icon size={13} />
            <span className="hidden sm:inline">{it.label}</span>
            {it.badge != null && it.badge > 0 && (
              <span className="ml-0.5 text-[10px] font-semibold bg-red-600 text-white rounded-full px-1.5 py-0 min-w-[16px] text-center leading-4">
                {it.badge}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="px-4 lg:px-6 py-2 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-1.5 text-slate-700 hover:text-slate-900 flex-shrink-0">
          <Building2 size={18} className="text-blue-600" />
          <span className="font-bold text-sm hidden sm:inline">REIP</span>
        </Link>

        <div className="h-5 w-px bg-slate-200" />

        <BusinessUnitSwitcher />

        <div className="h-5 w-px bg-slate-200" />

        <nav className="flex items-center gap-3 flex-1 overflow-x-auto">
          <NavGroup
            label="Negócio"
            items={[
              { to: '/', icon: Home, label: 'Dashboard', hint: 'Dashboard de empresas/ofertas' },
            ]}
          />
          <NavGroup
            label="Cadastro"
            items={[
              { to: '/contatos', icon: Users, label: 'Contatos', hint: 'Lista global de contatos' },
              { to: '/imoveis', icon: MapPin, label: 'Imóveis', hint: 'Lista global de imóveis' },
              { to: '/listas', icon: ListPlus, label: 'Listas', hint: 'Listas de inclusão, anti-listas e supressões' },
            ]}
          />
          <NavGroup
            label="Operação"
            items={[
              { to: '/inbox/triage', icon: MailCheck, label: 'Triage', hint: 'Bancada de triage de emails pendentes (Inbox 2.0)' },
              { to: '/sync', icon: Activity, label: 'Sync', hint: 'Observabilidade do Gmail Sync' },
              { to: '/fila-empresas', icon: ListChecks, label: 'Fila Empresas', hint: 'Fuzzy match pendente de revisão humana' },
              { to: '/intake', icon: Inbox, label: 'Intake', hint: 'Operações e fatos propostos por documentos', badge: pendingCount },
              { to: '/review', icon: ClipboardCheck, label: 'Review', hint: 'Workbench de revisão: propostas AI + triage WhatsApp/chamadas' },
              { to: '/relationships', icon: Network, label: 'Relacionamentos', hint: 'Hub de relacionamentos contato ↔ empresa/imóvel' },
            ]}
          />
          {hasLaunches && (
            <NavGroup
              label="Launches"
              items={[
                { to: '/launches', icon: Rocket, label: 'Painel', hint: 'Dashboard Launches' },
                { to: '/launches/deals', icon: Briefcase, label: 'Deals', hint: 'Pipeline de empreendimentos' },
                { to: '/launches/empreendimentos', icon: Building2, label: 'Empreendimentos', hint: 'Catálogo de empreendimentos' },
              ]}
            />
          )}
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          <QuickCreateDropdown />
          <div className="hidden md:flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded text-[11px] text-blue-900">
            <UserIcon size={12} /> {user?.email?.split('@')[0]}
          </div>
          <button
            onClick={signOut}
            className="px-2 py-1.5 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 rounded flex items-center gap-1 text-xs"
            title="Sair"
          >
            <LogOut size={13} />
          </button>
        </div>
      </div>

      {(title || subtitle) && (
        <div className="px-4 lg:px-6 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2">
          <Link to="/" className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1">
            <ArrowLeft size={11} />
          </Link>
          {TitleIcon && <TitleIcon size={14} className="text-slate-600" />}
          <h1 className="text-sm font-semibold text-slate-800">{title}</h1>
          {subtitle && <span className="text-[11px] text-slate-500">· {subtitle}</span>}
        </div>
      )}
    </header>
  );
}
